import {
  base_colorAction,
  base_colorActionHover,
  base_colorContentBorder,
  base_colorSurface,
  base_contentActive,
  base_contentBackground,
  text_colorTextPrimary,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import {
  font_2xl,
  font_family_weight_medium,
  font_family_weight_regular,
  font_md,
  font_size_md,
  font_sm,
} from 'ext-ui/style/typography'
import { css } from 'styled-components'

// The numbered/checked circle. Sized larger than the title's line so it reads as
// a distinct marker; the title is nudged down (see Stepper-content padding-top)
// to keep its first line centred against the circle.
const BADGE_SIZE = spacing(6.5) // 26px
// Matches the font_md line-height — used to centre the badge on the title.
const TITLE_LINE_HEIGHT = spacing(4.5) // 18px

export default css`
  .Stepper-heading {
    ${font_2xl};
    font-family: ${font_family_weight_medium};
    /* Reset the heading UA default (bold) so the Rubik-Medium face isn't
       faux-bolded on top of its intended 500 weight. */
    font-weight: normal;
    color: ${text_colorTextPrimary};
    margin: 0 0 ${spacing(5)} 0;
  }

  .Stepper-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &[data-orientation='horizontal'] {
    overflow-x: auto;
    padding: ${spacing(3)} ${spacing(10)};
    border-bottom: 1px solid ${base_colorContentBorder};
  }

  &[data-orientation='horizontal'] .Stepper-list {
    display: flex;
    align-items: center;
    gap: ${spacing(2)};
    min-width: max-content;
  }

  .Stepper-item {
    list-style: none;
  }

  &[data-orientation='horizontal'] .Stepper-item {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: ${spacing(2)};
  }

  /* Shared row layout for both the static and the tappable (completed) variants
     so the connector rail stretches the same way in either. The button variant
     resets the native button chrome back to this row. */
  .Stepper-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: ${spacing(3)};
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    text-align: start;
    font: inherit;
    color: inherit;
  }

  button.Stepper-row {
    cursor: pointer;
  }

  button.Stepper-row:focus-visible {
    outline: 2px solid ${base_colorAction};
    outline-offset: 2px;
    border-radius: ${spacing(1)};
  }

  /* Horizontal rows have no full-height connector to stretch against (the
     connector is a sibling of the row, not inside the rail), so centre the
     badge and label against each other instead of stretching/top-aligning the
     label. Vertical keeps the stretch above so its in-rail connector spans the
     row height. */
  &[data-orientation='horizontal'] .Stepper-row {
    align-items: center;
  }

  /* Left rail: the circle stacked above the connector that links to the next
     step. flex: 1 on the connector makes it grow to fill the row's height, so it
     always reaches the next circle regardless of how tall the content is. */
  .Stepper-rail {
    flex: 0 0 ${BADGE_SIZE};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &[data-orientation='horizontal'] .Stepper-rail {
    flex: 0 0 auto;
  }

  .Stepper-badge {
    box-sizing: border-box;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${BADGE_SIZE};
    height: ${BADGE_SIZE};
    border-radius: 50%;
    font-family: ${font_family_weight_medium};
    font-size: ${font_size_md};
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &[data-orientation='horizontal'] .Stepper-badge {
    width: ${spacing(6)};
    height: ${spacing(6)};
    ${font_sm};
  }

  /* No vertical margins: the line runs flush from one circle's edge to the
     next so the steps read as connected. */
  .Stepper-connector {
    flex: 1 0 auto;
    width: 2px;
    border-radius: 1px;
    background: ${base_colorContentBorder};
  }

  &[data-orientation='horizontal'] .Stepper-connector {
    flex: 0 0 ${spacing(6)};
    width: ${spacing(6)};
    height: 2px;
  }

  .Stepper-content {
    display: flex;
    flex-direction: column;
    gap: ${spacing(1)};
    /* Centre the title's first line against the taller circle. */
    padding-top: calc((${BADGE_SIZE} - ${TITLE_LINE_HEIGHT}) / 2);
  }

  &[data-orientation='horizontal'] .Stepper-content {
    padding-top: 0;
  }

  /* Vertical rhythm between steps. Lives on the content (not the <li>) so it's
     inside the flex row's height, letting the connector span the full gap. */
  .Stepper-item:not(:last-child) .Stepper-content {
    padding-bottom: ${spacing(9)};
  }

  &[data-orientation='horizontal']
    .Stepper-item:not(:last-child)
    .Stepper-content {
    padding-bottom: 0;
  }

  .Stepper-title {
    ${font_md};
    font-family: ${font_family_weight_regular};
    color: ${text_colorTextPrimary};
    transition: color 0.15s ease;
  }

  &[data-orientation='horizontal'] .Stepper-title {
    ${font_sm};
    white-space: nowrap;
    color: ${text_colorTextSecondary};
  }

  .Stepper-subtitle {
    ${font_sm};
    color: ${text_colorTextSecondary};
  }

  /* The active step is the only one weighted at 500 (medium). */
  .Stepper-item[data-state='active'] .Stepper-title {
    font-family: ${font_family_weight_medium};
  }

  &[data-orientation='horizontal']
    .Stepper-item[data-state='active']
    .Stepper-title {
    color: ${text_colorTextPrimary};
  }

  /* Circle colours carry the state. Each state also has a non-colour cue
     (number vs. check, aria-current) so colour is never the sole signal. */
  .Stepper-item[data-state='upcoming'] .Stepper-badge {
    background: ${base_colorSurface};
    border: 1px solid ${base_colorContentBorder};
    color: ${text_colorTextSecondary};
  }

  /* KNOWN A11Y ISSUE (pending design): white on base_colorAction is ~3.4:1,
     under the 4.5:1 AA bar for the 14px medium number (not bold, so the 3:1
     large-text exception doesn't apply). The number is aria-hidden and
     redundant (position + aria-current convey the step), so this ships as a
     decorative exception for now. Awaiting design input on the proper fix
     (e.g. a darker active fill); to be resolved in a follow-up. */
  .Stepper-item[data-state='active'] .Stepper-badge {
    background: ${base_colorAction};
    color: ${base_contentBackground};
  }

  .Stepper-item[data-state='completed'] .Stepper-badge {
    background: ${base_contentActive};
    color: ${base_colorAction};
  }

  /* Completed segments of the track fill blue to read as progress. */
  .Stepper-item[data-state='completed'] .Stepper-connector {
    background: ${base_colorAction};
  }

  /* Completed steps are tappable to jump back; shift the label to the action
     colour on hover as the interactive cue. */
  button.Stepper-row:hover .Stepper-title {
    color: ${base_colorActionHover};
  }

  @media (prefers-reduced-motion: reduce) {
    .Stepper-title {
      transition: none;
    }
  }
`
